import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useVaultWarning } from "@/context/VaultWarningContext";
import { useMode } from "@/hooks/useMode";
import { useSoundSettings } from "@/hooks/useSoundSettings";
import Header from "@/components/Header";
import TimerCard from "@/components/TimerCard";
import CompactTimerCard from "@/components/CompactTimerCard";
import CardDialog from "@/components/CardDialog";
import { SimpleDialog } from "@/components/ui/simple-dialog";
import BoardTracker from "@/components/BoardTracker";
import VaultDoorTimer from "@/components/VaultDoorTimer";
import ExecHangarStatus from "@/components/ExecHangarStatus";
import ShipClaimsDialog from "@/components/ShipClaimsDialog";
import CardHoldersTab from "@/components/CardHoldersTab";
import RecentClaimsFeed from "@/components/RecentClaimsFeed";
import ProxyClaimDialog from "@/components/ProxyClaimDialog";
import ActiveUsersDialog from "@/components/ActiveUsersDialog";
import { Plus, ListFilter, Inbox, Sparkles, LayoutGrid, Rows3, RefreshCw, ClipboardList, Settings2, X, ShieldCheck, Rocket, CreditCard, Users, Pencil, Check } from "lucide-react";
import { PRESETS, PRESET_BY_SOURCE } from "@/data/presets";
import { scheduleCardNotifications } from "@/lib/swNotifications";



const ADMIN_EMAIL = "liamdevlin@live.com";

const SORTS = [
  { id: "soonest", label: "Soonest" },
  { id: "longest", label: "Longest" },
];

function playReadySound(volume = 70, pitch = 880) {
  try {
    const gainValue = Math.max(0.0001, (volume / 100) * 0.3);
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(pitch, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(Math.min(pitch * 1.5, 1320), ctx.currentTime + 0.18);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(gainValue, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
    o.start();
    o.stop(ctx.currentTime + 0.55);
  } catch (e) {
    // no-op
  }
}

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.email === ADMIN_EMAIL;
  useVaultWarning(); // ensure context is consumed (CSS vars managed by context)
  const { mode, setMode, modeLoading } = useMode();
  const { soundEnabled, soundVolume, soundPitch } = useSoundSettings();

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sort, setSort] = useState("soonest");
  const [proxyClaimOpen, setProxyClaimOpen] = useState(false);
  const [proxyClaimCard, setProxyClaimCard] = useState(null);
  const [proxyClaimBoardKey, setProxyClaimBoardKey] = useState(null);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [bulkScope, setBulkScope] = useState(null); // 'all' | 'ready' | null
  const [view, setView] = useState("detailed"); // 'detailed' | 'overview'
  const [activeTab, setActiveTab] = useState("board"); // 'board' | 'activity' | 'ship-claims' | 'admin'
  const [activeUsersOpen, setActiveUsersOpen] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [whitelistEntries, setWhitelistEntries] = useState([]); // [{id, email, nickname}]
  const [whitelistInput, setWhitelistInput] = useState("");
  const [whitelistSaving, setWhitelistSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const boardTrackerRef = useRef(null);

  // Derive the current user's nickname from the whitelist, falling back to email.
  // whitelistEntries is populated on mount (for all users) via loadWhitelist.
  const userNickname = useMemo(() => {
    if (!user?.email) return null;
    const entry = whitelistEntries.find((e) => e.email === user.email);
    return entry?.nickname && entry.nickname !== user.email ? entry.nickname : (user.email ?? null);
  }, [user, whitelistEntries]);

  // Resolves the current user's nickname reliably at claim time.
  // If whitelistEntries is already populated (the common case after mount),
  // the lookup is synchronous. If the whitelist hasn't loaded yet (e.g. the
  // user claims very quickly after page load), we fetch it fresh so the
  // nickname is never saved as null or an empty string.
  const resolveNickname = useCallback(async () => {
    if (!user?.email) return "Unknown";

    // Fast path: whitelist already in state
    if (whitelistEntries.length > 0) {
      const entry = whitelistEntries.find((e) => e.email === user.email);
      return entry?.nickname || user.email;
    }

    // Slow path: whitelist not yet loaded — fetch it now and cache the result
    try {
      const { data } = await api.get("/users/whitelist");
      const fresh = Array.isArray(data) ? data : [];
      setWhitelistEntries(fresh);
      const entry = fresh.find((e) => e.email === user.email);
      return entry?.nickname || user.email;
    } catch {
      return user.email;
    }
  }, [user, whitelistEntries]);

  const loadActivityLog = useCallback(async () => {
    setActivityLoading(true);
    try {
      const { data } = await api.get("/activity-log");
      setActivityLog(data);
    } catch (e) {
      // silently ignore
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const loadCards = useCallback(async () => {
    try {
      const { data } = await api.get("/cards");
      setCards(data);
    } catch (e) {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWhitelist = useCallback(async () => {
    try {
      const { data } = await api.get("/users/whitelist");
      setWhitelistEntries(data ?? []);
    } catch (e) {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    loadCards();
    // Shared state — poll for updates from other operators
    const id = setInterval(() => {
      loadCards();
      boardTrackerRef.current?.refresh?.();
    }, 4000);
    const onFocus = () => {
      if (document.visibilityState === "visible") {
        loadCards();
        boardTrackerRef.current?.refresh?.();
      }
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [loadCards]);

  // Re-fetch cards and board counts whenever the user switches mode so the
  // board immediately reflects the new mode's state without waiting for the
  // next poll cycle.
  useEffect(() => {
    if (mode != null) {
      loadCards();
      boardTrackerRef.current?.refresh?.();
    }
  }, [mode, loadCards]);

  // Log out the user when they close the tab or navigate away so sessions
  // don't persist after the browser window is shut.
  // Uses fetch with keepalive so the request outlives the page and credentials
  // (session cookie) are included — sendBeacon doesn't support credentials.
  //
  // Strategy:
  //   Desktop — beforeunload + pagehide are reliable; visibilitychange is NOT
  //             used because switching tabs would incorrectly trigger logout.
  //   Mobile  — beforeunload/pagehide often don't fire when the browser is
  //             backgrounded, so visibilitychange is used as the fallback,
  //             but only on touch-capable / mobile devices.
  useEffect(() => {
    const logoutUrl = `${API}/auth/logout`;

    // Detect mobile: primary signal is coarse pointer (touch screen).
    // Falls back to user-agent sniffing as a secondary check.
    const isMobile =
      window.matchMedia("(pointer: coarse)").matches ||
      /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

    const doLogout = () => {
      fetch(logoutUrl, {
        method: "POST",
        keepalive: true,
        credentials: "include",
      }).catch(() => {
        // best-effort — ignore errors on page unload
      });
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        doLogout();
      }
    };

    window.addEventListener("beforeunload", doLogout);
    window.addEventListener("pagehide", doLogout);
    if (isMobile) {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    return () => {
      window.removeEventListener("beforeunload", doLogout);
      window.removeEventListener("pagehide", doLogout);
      if (isMobile) {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, []);

  // Load whitelist on mount for all users so userNickname is available
  // immediately (e.g. for the ProxyClaimDialog "Claimed by (you)" field).
  useEffect(() => {
    loadWhitelist();
  }, [loadWhitelist]);

  // Reload whitelist when admin tab is opened to pick up any recent changes
  useEffect(() => {
    if (activeTab === "admin" && isAdmin) {
      loadWhitelist();
    }
  }, [activeTab, isAdmin, loadWhitelist]);

  // Push card list into the service worker so it can fire timestamp-triggered
  // notifications even if the tab is backgrounded/closed (where supported).
  useEffect(() => {
    if (loading) return;
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      scheduleCardNotifications(cards);
    }
  }, [cards, loading, notifPermission]);

  const requestNotif = async (action) => {
    if (typeof Notification === "undefined") return;
    if (action === "disable") {
      setNotifPermission("denied");
      return;
    }
    const res = await Notification.requestPermission();
    setNotifPermission(res);
    // notification granted — no toast
  };

  const handleAddWhitelistEmail = () => {
    const email = whitelistInput.trim().toLowerCase();
    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return;
    }
    if (whitelistEntries.some((e) => e.email === email)) {
      return;
    }
    // Optimistically add with a temporary id; the real id comes after save
    setWhitelistEntries((prev) => [...prev, { id: null, email, nickname: email }]);
    setWhitelistInput("");
  };

  const handleRemoveWhitelistEmail = (email) => {
    setWhitelistEntries((prev) => prev.filter((e) => e.email !== email));
  };

  const handleSaveWhitelist = async () => {
    setWhitelistSaving(true);
    try {
      const { data } = await api.put("/settings/whitelist", {
        emails: whitelistEntries.map(({ email, nickname }) => ({ email, nickname: nickname || email })),
      });
      setWhitelistEntries(data ?? []);
      // Close any open dialogs and switch back to board tab after successful save
      setActiveUsersOpen(false);
      setActiveTab("board");
    } catch (e) {
      // silently ignore
    } finally {
      setWhitelistSaving(false);
    }
  };

  const handleUpdateNickname = async (id, nickname) => {
    try {
      const { data } = await api.put(`/users/whitelist/${id}`, { nickname });
      setWhitelistEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, nickname: data.nickname ?? nickname } : e))
      );
    } catch (e) {
      throw e;
    }
  };

  const handleSubmit = async (payload) => {
    setDialogError(null);
    try {
      if (editing?.id) {
        const { data } = await api.put(`/cards/${editing.id}`, { ...payload, notified: false });
        setCards((prev) => prev.map((c) => (c.id === data.id ? data : c)));
        setEditing(null);
        if (activeTab === "activity") loadActivityLog();
        return data;
      } else {
        // Include the current mode so the card is created in the right bucket.
        // The backend also derives mode from the user's stored preference, but
        // sending it explicitly keeps the client and server in sync.
        const { data } = await api.post("/cards", { ...payload, mode: mode ?? "group" });
        setCards((prev) => [...prev, data]);
        setEditing(null);
        if (activeTab === "activity") loadActivityLog();
        return data;
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        setDialogError(e.response.data?.detail ?? "A timer for this card is already active");
      } else {
        // silently ignore
      }
      throw e;
    }
  };

  const handleDelete = async (card) => {
    try {
      await api.delete(`/cards/${card.id}`);
      setCards((prev) => prev.filter((c) => c.id !== card.id));
      if (activeTab === "activity") loadActivityLog();
    } catch (e) {
      // silently ignore
    }
  };

  const handleBulkDelete = async () => {
    const scope = bulkScope;
    if (!scope) return;
    try {
      const { data } = await api.delete(`/cards?scope=${scope}`);
      if (scope === "all") {
        setCards([]);
      } else {
        const now = Date.now();
        setCards((prev) => prev.filter((c) => new Date(c.target_at).getTime() > now));
      }
      if (activeTab === "activity") loadActivityLog();
    } catch (e) {
      // silently ignore
    } finally {
      setBulkScope(null);
    }
  };

  const handlePresetClick = (preset) => {
    setDialogError(null);
    setEditing({
      source: preset.source,
      options: preset.cardOptions,
      defaultMinutes: preset.defaultMinutes ?? 30,
    });
    setDialogOpen(true);
  };

  const handleReset = async (card) => {
    const minutes = PRESET_BY_SOURCE[card.source]?.defaultMinutes ?? 30;
    const target = new Date(Date.now() + minutes * 60 * 1000).toISOString();
    try {
      const { data } = await api.put(`/cards/${card.id}`, {
        target_at: target,
        notified: false,
      });
      setCards((prev) => prev.map((c) => (c.id === data.id ? data : c)));
    } catch (e) {
      // silently ignore
    }
  };

  const handleClaim = async (card, boardKey) => {
    const minutes = PRESET_BY_SOURCE[card.source]?.defaultMinutes ?? 30;
    const target = new Date(Date.now() + minutes * 60 * 1000).toISOString();
    try {
      // Resolve the nickname before touching the card so we always have a
      // valid value even if the whitelist hadn't finished loading on mount.
      const nickname = await resolveNickname();

      // Reset the card cooldown to default; clear notified flag.
      const { data } = await api.put(`/cards/${card.id}`, {
        target_at: target,
        notified: false,
      });
      setCards((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      // Record the direct claim in the card-holders log.
      await api.post("/card-holders", {
        cardName: card.name,
        location: card.source || "Unknown",
        claimedBy: nickname,
        mode: mode ?? "group",
      });
      // If it's a Board card, also bump the board count.
      if (boardKey) {
        await boardTrackerRef.current?.increment(boardKey);
      }
    } catch (e) {
      // silently ignore
    }
  };

  // Opens the proxy claim dialog — called by TimerCard / CompactTimerCard
  // when the user middle-clicks or long-presses the claim button.
  // Proxy claims are only supported in group mode; silently ignore in private.
  const handleOpenProxyClaim = (card, boardKey) => {
    if (mode === "private") return;
    setProxyClaimCard(card);
    setProxyClaimBoardKey(boardKey ?? null);
    setProxyClaimOpen(true);
  };

  // Called by ProxyClaimDialog when the operator confirms the proxy claim.
  // Resets the card timer (same as a normal claim) and records the proxy
  // claim in the card-holders log.
  const handleProxyClaimConfirm = async (card, targetNickname) => {
    // targetNickname comes from the ProxyClaimDialog dropdown. Guard against
    // it being empty/null — the dialog should prevent this, but be defensive.
    if (!targetNickname?.trim()) {
      throw new Error("No target user selected for proxy claim.");
    }

    const minutes = PRESET_BY_SOURCE[card.source]?.defaultMinutes ?? 30;
    const target = new Date(Date.now() + minutes * 60 * 1000).toISOString();

    // Resolve the proxy operator's nickname before touching the card so we
    // always have a valid value even if the whitelist hadn't loaded on mount.
    const proxyNickname = await resolveNickname();

    // Reset the card cooldown
    const { data } = await api.put(`/cards/${card.id}`, {
      target_at: target,
      notified: false,
    });
    setCards((prev) => prev.map((c) => (c.id === data.id ? data : c)));

    // Record the proxy claim in the card-holders log
    await api.post("/card-holders/proxy", {
      cardName: card.name,
      location: card.source || "Unknown",
      claimedBy: targetNickname.trim(),
      proxyClaimedBy: proxyNickname,
      mode: mode ?? "group",
    });

    // If it's a Board card, also bump the board count
    if (proxyClaimBoardKey) {
      await boardTrackerRef.current?.increment(proxyClaimBoardKey);
    }
  };

  const handleReadyTransition = useCallback(
    async (card) => {
      // mark notified on server so we don't retrigger on reload
      try {
        await api.put(`/cards/${card.id}`, { notified: true });
        setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, notified: true } : c)));
      } catch (e) {}
      // Play sound if enabled
      if (soundEnabled) {
        playReadySound(soundVolume, soundPitch);
      }
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        try {
          new Notification("EXEC/HANGAR — Card Ready", {
            body: `${card.name} is now available.`,
            silent: false,
          });
        } catch (e) {}
      }
    },
    [soundEnabled, soundVolume, soundPitch]
  );

  const sorted = useMemo(() => {
    const arr = [...cards];
    const now = Date.now();
    switch (sort) {
      case "soonest":
        arr.sort((a, b) => new Date(a.target_at) - new Date(b.target_at));
        break;
      case "longest":
        arr.sort((a, b) => new Date(b.target_at) - new Date(a.target_at));
        break;
      default:
        break;
    }
    return arr;
  }, [cards, sort]);

  const readyCount = useMemo(
    () => cards.filter((c) => new Date(c.target_at).getTime() <= Date.now()).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cards, Math.floor(Date.now() / 60000)]
  );

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white">
      <Header
        notifPermission={notifPermission}
        onRequestNotif={requestNotif}
        totalCards={cards.length}
        readyCount={readyCount}
        onBulkScope={setBulkScope}
        onUseSet={() => boardTrackerRef.current?.decrementAll()}
        onGetBoardCounts={() => boardTrackerRef.current?.getCounts?.() ?? {}}
        mode={mode}
        onModeToggle={setMode}
        userNickname={userNickname}
        soundEnabled={soundEnabled}
      />

      <div className="bg-grid">
        <main className="max-w-7xl mx-auto px-6 lg:px-10 py-8 pb-48">
          {/* Top row */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="text-[11px] tracking-[0.4em] uppercase text-orange-500">
                  / Operator Console
                </div>
                {mode != null && (
                  <span
                    data-testid="mode-indicator"
                    className={
                      mode === "private"
                        ? "text-[9px] tracking-widest uppercase font-mono px-2 py-0.5 border border-emerald-500/50 text-emerald-400 bg-emerald-500/5"
                        : "text-[9px] tracking-widest uppercase font-mono px-2 py-0.5 border border-orange-500/50 text-orange-400 bg-orange-500/5"
                    }
                  >
                    {mode === "private" ? "⬡ Private" : "⬡ Group"}
                  </span>
                )}
              </div>
              <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-slate-300">
                The Library <span className="text-orange-500" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", background: 'linear-gradient(135deg, rgba(217,119,6,0.9), rgba(234,88,12,1))', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: 'none' }}>/</span> Executive Hangar <span className="text-orange-500" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", background: 'linear-gradient(135deg, rgba(217,119,6,0.9), rgba(234,88,12,1))', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: 'none' }}>Board</span>
              </h1>
              <p className="text-slate-400 mt-2 text-sm">
                Live countdowns for every access card you're tracking.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <ExecHangarStatus />
            </div>
          </div>

          {/* Board Tracker */}
          <div className="mb-3">
            <BoardTracker ref={boardTrackerRef} mode={mode ?? "group"} />
          </div>

          {/* Ruin Vault Door cyclic timer */}
          <div className="mb-4">
            <VaultDoorTimer />
          </div>

          {/* Tab bar — Board / Ship Claims / Card Holders / Activity / Admin */}
          <div className="flex items-center gap-1 mb-6 border-b border-slate-800" data-testid="tab-bar">
            <button
              onClick={() => setActiveTab("board")}
              data-testid="tab-board"
              className={`flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase border-b-2 transition-colors -mb-px ${
                activeTab === "board"
                  ? "border-orange-500 text-orange-400"
                  : "border-transparent text-slate-500 hover:text-slate-300"
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              Board
            </button>
            <button
              onClick={() => setActiveTab("ship-claims")}
              data-testid="tab-ship-claims"
              className={`flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase border-b-2 transition-colors -mb-px ${
                activeTab === "ship-claims"
                  ? "border-orange-500 text-orange-400"
                  : "border-transparent text-slate-500 hover:text-slate-300"
              }`}
            >
              <Rocket className="w-3.5 h-3.5" />
              Ship Claims
            </button>
            <button
              onClick={() => setActiveTab("card-holders")}
              data-testid="tab-card-holders"
              className={`flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase border-b-2 transition-colors -mb-px ${
                activeTab === "card-holders"
                  ? "border-orange-500 text-orange-400"
                  : "border-transparent text-slate-500 hover:text-slate-300"
              }`}
            >
              <CreditCard className="w-3.5 h-3.5" />
              Card Holders
            </button>
            {isAdmin && (
              <>
                {/* Divider between public and admin tabs */}
                <div className="w-px h-5 bg-slate-700 mx-2 self-center" aria-hidden="true" />
                <button
                  onClick={() => {
                    setActiveTab("activity");
                    loadActivityLog();
                  }}
                  data-testid="tab-activity"
                  className={`flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase border-b-2 transition-colors -mb-px ${
                    activeTab === "activity"
                      ? "border-orange-500 text-orange-400"
                      : "border-transparent text-slate-500 hover:text-slate-300"
                  }`}
                >
                  <ClipboardList className="w-3.5 h-3.5" />
                  Activity
                </button>
                <button
                  onClick={() => setActiveTab("admin")}
                  data-testid="tab-admin"
                  className={`flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase border-b-2 transition-colors -mb-px ${
                    activeTab === "admin"
                      ? "border-orange-500 text-orange-400"
                      : "border-transparent text-slate-500 hover:text-slate-300"
                  }`}
                >
                  <Settings2 className="w-3.5 h-3.5" />
                  Admin
                </button>
              </>
            )}
          </div>

          {activeTab === "activity" && isAdmin ? (
            <ActivityLogPanel
              log={activityLog}
              loading={activityLoading}
              onRefresh={loadActivityLog}
            />
          ) : activeTab === "ship-claims" ? (
            <ShipClaimsDialog isAdmin={isAdmin} mode={mode} />
          ) : activeTab === "card-holders" ? (
            <CardHoldersTab
              isAdmin={isAdmin}
              mode={mode}
              currentUser={user ? { email: user.email, nickname: userNickname } : null}
              whitelistEntries={whitelistEntries}
            />
          ) : activeTab === "admin" && isAdmin ? (
            <>
              <AdminSettingsPanel
                whitelistEntries={whitelistEntries}
                whitelistInput={whitelistInput}
                onWhitelistInputChange={setWhitelistInput}
                onAddWhitelistEmail={handleAddWhitelistEmail}
                onRemoveWhitelistEmail={handleRemoveWhitelistEmail}
                onSaveWhitelist={handleSaveWhitelist}
                whitelistSaving={whitelistSaving}
                onOpenActiveUsers={() => setActiveUsersOpen(true)}
                onUpdateNickname={handleUpdateNickname}
              />
              <ActiveUsersDialog
                open={activeUsersOpen}
                onOpenChange={setActiveUsersOpen}
              />
            </>
          ) : (
            <>
          {/* Presets strip */}
          <div className="mb-6" data-testid="presets-strip">
            <div className="flex items-center gap-1.5 mb-3">
              <Sparkles className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-[11px] tracking-[0.3em] uppercase text-slate-500">
                Quick Add — Pyro Executive Cards
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handlePresetClick(p)}
                  data-testid={`preset-${p.id}`}
                  className="flex-1 basis-[140px] group flex items-center justify-center gap-1.5 px-3 py-2 border border-slate-800 bg-[#121822]/60 hover:border-orange-500/60 hover:bg-orange-500/5 text-slate-300 hover:text-orange-300 transition-colors text-sm tracking-wider"
                >
                  <Plus className="w-3 h-3 text-slate-500 group-hover:text-orange-400" />
                  <span className="group-hover:[text-shadow:0_0_6px_rgba(249,115,22,0.5)]">{p.source.split(",")[0]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Sort bar + view toggle */}
          {cards.length > 0 && (
            <div className="flex items-center justify-between gap-4 mb-4 flex-wrap" data-testid="sort-bar">
              <div className="flex items-center gap-2 flex-wrap">
                <ListFilter className="w-4 h-4 text-slate-500" />
                <span className="text-[11px] tracking-[0.3em] uppercase text-slate-500 mr-2">Sort</span>
                {SORTS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSort(s.id)}
                    data-testid={`sort-${s.id}`}
                    className={`px-3 py-1.5 border text-[11px] tracking-widest uppercase transition-colors ${
                      sort === s.id
                        ? "border-orange-500 bg-orange-500/10 text-orange-400"
                        : "border-slate-800 text-slate-400 hover:border-slate-600"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1" data-testid="view-toggle">
                <button
                  onClick={() => setView("detailed")}
                  data-testid="view-detailed"
                  aria-label="Detailed view"
                  className={`flex items-center gap-1.5 px-3 py-1.5 border text-[11px] tracking-widest uppercase transition-colors ${
                    view === "detailed"
                      ? "border-orange-500 bg-orange-500/10 text-orange-400"
                      : "border-slate-800 text-slate-400 hover:border-slate-600"
                  }`}
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  Detailed
                </button>
                <button
                  onClick={() => setView("overview")}
                  data-testid="view-overview"
                  aria-label="Overview"
                  className={`flex items-center gap-1.5 px-3 py-1.5 border text-[11px] tracking-widest uppercase transition-colors ${
                    view === "overview"
                      ? "border-orange-500 bg-orange-500/10 text-orange-400"
                      : "border-slate-800 text-slate-400 hover:border-slate-600"
                  }`}
                >
                  <Rows3 className="w-3.5 h-3.5" />
                  Overview
                </button>
              </div>
            </div>
          )}

          {/* Grid */}
          {loading ? (
            <div className="text-orange-400 text-xs tracking-widest uppercase animate-pulse">
              Loading board…
            </div>
          ) : sorted.length === 0 ? (
            <EmptyState />
          ) : view === "overview" ? (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3"
              data-testid="overview-grid"
            >
              {sorted.map((c, i) => (
                <div key={c.id} className="animate-slide-up" style={{ animationDelay: `${i * 25}ms` }}>
                  <CompactTimerCard
                    card={c}
                    onClick={(card) => {
                      setDialogError(null);
                      setEditing(card);
                      setDialogOpen(true);
                    }}
                    onClaim={handleClaim}
                    onProxyClaim={handleOpenProxyClaim}
                    onReadyTransition={handleReadyTransition}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
              data-testid="cards-grid"
            >
              {sorted.map((c, i) => (
                <div key={c.id} className="animate-slide-up" style={{ animationDelay: `${i * 40}ms` }}>
                  <TimerCard
                    card={c}
                    onEdit={(card) => {
                      setDialogError(null);
                      setEditing(card);
                      setDialogOpen(true);
                    }}
                    onDelete={handleDelete}
                    onReset={handleReset}
                    onClaim={handleClaim}
                    onProxyClaim={handleOpenProxyClaim}
                    onReadyTransition={handleReadyTransition}
                  />
                </div>
              ))}
            </div>
          )}
            </>
          )}
        </main>
      </div>

      {dialogOpen && (
        <CardDialog
          open={dialogOpen}
          onOpenChange={(v) => {
            setDialogOpen(v);
            if (!v) setEditing(null);
          }}
          initial={editing}
          onSubmit={handleSubmit}
          onClaim={handleClaim}
          error={dialogError}
          onErrorClear={() => setDialogError(null)}
          mode={mode}
        />
      )}

      {proxyClaimOpen && proxyClaimCard && (
        <ProxyClaimDialog
          open={proxyClaimOpen}
          onOpenChange={(v) => {
            setProxyClaimOpen(v);
            if (!v) {
              setProxyClaimCard(null);
              setProxyClaimBoardKey(null);
            }
          }}
          card={proxyClaimCard}
          currentUser={user}
          userNickname={userNickname}
          onConfirm={handleProxyClaimConfirm}
          mode={mode}
        />
      )}

      {bulkScope && (
        <SimpleDialog open={!!bulkScope} onOpenChange={(v) => !v && setBulkScope(null)}>
          <div
            className="bg-[#0B0E14]/95 backdrop-blur-xl border border-orange-500/30 rounded-none w-full max-w-md text-white p-4"
            data-testid="bulk-confirm-dialog"
          >
            <div className="mb-3">
              <div className="text-[11px] tracking-[0.3em] uppercase text-orange-400 mb-1">
                Confirm Action
              </div>
              <div className="font-display text-2xl tracking-tight">
                {bulkScope === "ready" ? "Clear Ready Cards?" : "Clear All Cards?"}
              </div>
              <div className="text-slate-400 text-sm mt-1">
                {bulkScope === "ready"
                  ? "Remove all cards that are currently ready. This action cannot be undone."
                  : "Remove all tracked cards. This action cannot be undone."}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setBulkScope(null)}
                className="px-4 py-2 border border-slate-700 text-slate-300 hover:border-slate-500 text-xs tracking-widest uppercase"
                data-testid="bulk-confirm-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkDelete}
                className={`px-5 py-2 border text-xs tracking-widest uppercase transition-colors ${
                  bulkScope === "ready"
                    ? "border-emerald-500 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500 hover:text-black"
                    : "border-red-500 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-black"
                }`}
                data-testid="bulk-confirm-submit"
              >
                {bulkScope === "ready" ? "Clear Ready" : "Clear All"}
              </button>
            </div>
          </div>
        </SimpleDialog>
      )}

      {/* Recent Claims Feed — fixed to bottom of screen */}
      <div className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none">
        <div className="pointer-events-auto bg-[#0B0E14]/95 backdrop-blur-sm empty:border-t-0 empty:bg-transparent">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <RecentClaimsFeed whitelistEntries={whitelistEntries} />
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="border border-dashed border-orange-500/25 p-12 text-center relative bg-[#121822]/40"
      data-testid="empty-state"
    >
      <div className="w-14 h-14 mx-auto border border-orange-500/30 flex items-center justify-center mb-4">
        <Inbox className="w-6 h-6 text-orange-400" />
      </div>
      <h3 className="font-display font-bold text-xl text-white mb-2">No cards on board</h3>
      <p className="text-slate-400 text-sm max-w-md mx-auto">
        Use the <span className="text-orange-400">Quick Add</span> chips above to start tracking a
        Pyro station card. We'll count it down to the second.
      </p>
    </div>
  );
}

const ACTION_STYLES = {
  CARD_CREATED: { color: "text-emerald-400", label: "Created" },
  CARD_UPDATED: { color: "text-orange-400", label: "Updated" },
  CARD_DELETED: { color: "text-red-400", label: "Deleted" },
  BOARD_CLEARED: { color: "text-amber-400", label: "Board Cleared" },
};

function ActivityLogPanel({ log, loading, onRefresh }) {
  if (loading) {
    return (
      <div className="text-orange-400 text-xs tracking-widest uppercase animate-pulse" data-testid="activity-loading">
        Loading activity log…
      </div>
    );
  }

  return (
    <div data-testid="activity-log-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] tracking-[0.3em] uppercase text-slate-500">
          Activity Log — last 100 events
        </div>
        <button
          onClick={onRefresh}
          data-testid="activity-refresh"
          className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-700 text-[11px] tracking-widest uppercase text-slate-400 hover:border-slate-500 hover:text-slate-200 transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {log.length === 0 ? (
        <div className="border border-dashed border-slate-700 p-10 text-center text-slate-500 text-sm">
          No activity recorded yet.
        </div>
      ) : (
        <div className="space-y-1" data-testid="activity-entries">
          {log.map((entry) => {
            const style = ACTION_STYLES[entry.action] ?? { color: "text-slate-400", label: entry.action };
            const ts = new Date(entry.timestamp);
            const dateStr = ts.toLocaleDateString(undefined, { month: "short", day: "numeric" });
            const timeStr = ts.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
            return (
              <div
                key={entry.id}
                className="flex items-start gap-4 px-4 py-3 border border-slate-800 bg-[#121822]/60 hover:bg-[#121822]/90 transition-colors"
                data-testid="activity-entry"
              >
                <div className="text-[10px] font-mono text-slate-600 whitespace-nowrap pt-0.5 min-w-[110px]">
                  {dateStr} {timeStr}
                </div>
                <div className={`text-[11px] tracking-widest uppercase font-bold whitespace-nowrap ${style.color}`}>
                  {style.label}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-slate-200 text-sm font-medium">{entry.card_name}</span>
                  {entry.details && (
                    <span className="text-slate-500 text-xs ml-2">{entry.details}</span>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {entry.mode && (
                    <span
                      data-testid="activity-mode-badge"
                      className={
                        entry.mode === "private"
                          ? "text-[9px] tracking-widest uppercase font-mono px-1.5 py-0.5 border border-emerald-500/40 text-emerald-400 bg-emerald-500/5"
                          : "text-[9px] tracking-widest uppercase font-mono px-1.5 py-0.5 border border-orange-500/40 text-orange-400 bg-orange-500/5"
                      }
                    >
                      {entry.mode}
                    </span>
                  )}
                  {entry.user_name && (
                    <div className="text-[10px] text-slate-600 whitespace-nowrap">
                      {entry.user_name}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WhitelistNicknameRow({ entry, onRemove, onUpdateNickname, editingId, setEditingId }) {
  const isEditing = editingId === entry.email;
  const [draft, setDraft] = useState(entry.nickname || entry.email);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Sync draft when entry.nickname changes externally (e.g. after save)
  useEffect(() => {
    if (!isEditing) {
      setDraft(entry.nickname || entry.email);
    }
  }, [entry.nickname, entry.email, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    setDraft(entry.nickname || entry.email);
    setError(null);
    setEditingId(entry.email);
  };

  const handleCancel = () => {
    setDraft(entry.nickname || entry.email);
    setError(null);
    setEditingId(null);
  };

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (trimmed === (entry.nickname || entry.email)) {
      setEditingId(null);
      return;
    }
    if (!entry.id) {
      // Entry not yet persisted — just update local state optimistically
      setEditingId(null);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onUpdateNickname(entry.id, trimmed);
      setEditingId(null);
    } catch (e) {
      setError(e?.response?.data?.detail ?? "Failed to save nickname");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  const isDisabled = !!editingId && !isEditing;

  return (
    <li
      className="border border-slate-700 bg-[#0B0E14]/60"
      data-testid="whitelist-entry"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        {/* Nickname column */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              data-testid="nickname-inline-input"
              className="w-full px-2 py-1 bg-[#0B0E14] border border-orange-500/60 text-white text-xs placeholder-slate-600 focus:outline-none focus:border-orange-500 transition-colors"
              placeholder="Nickname…"
            />
          ) : (
            <button
              onClick={handleStartEdit}
              disabled={isDisabled}
              data-testid="nickname-display"
              title="Click to edit nickname"
              className="group flex items-center gap-1.5 text-left w-full disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="text-slate-200 text-xs font-medium truncate">
                {entry.nickname && entry.nickname !== entry.email ? entry.nickname : (
                  <span className="text-slate-500 italic">No nickname</span>
                )}
              </span>
              <Pencil className="w-2.5 h-2.5 text-slate-600 group-hover:text-orange-400 flex-shrink-0 transition-colors" />
            </button>
          )}
          {/* Email sub-label */}
          <span className="block text-slate-500 text-[10px] font-mono truncate mt-0.5">
            {entry.email}
          </span>
          {error && (
            <span className="block text-orange-400 text-[10px] mt-0.5">{error}</span>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving || !draft.trim()}
                data-testid="nickname-save"
                title="Save nickname"
                className="flex items-center gap-1 px-2 py-1 border border-orange-500/60 bg-orange-500/10 text-orange-300 hover:bg-orange-500/20 transition-colors text-[10px] tracking-widest uppercase disabled:opacity-40"
              >
                <Check className="w-3 h-3" />
                {saving ? "…" : "Save"}
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                data-testid="nickname-cancel"
                title="Cancel"
                className="flex items-center px-2 py-1 border border-slate-600 bg-slate-800/60 text-slate-400 hover:text-slate-200 transition-colors text-[10px] tracking-widest uppercase disabled:opacity-40"
              >
                <X className="w-3 h-3" />
              </button>
            </>
          ) : (
            <button
              onClick={() => onRemove(entry.email)}
              disabled={isDisabled}
              data-testid="whitelist-remove"
              aria-label={`Remove ${entry.email}`}
              className="text-slate-600 hover:text-orange-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function AdminSettingsPanel({
  whitelistEntries,
  whitelistInput,
  onWhitelistInputChange,
  onAddWhitelistEmail,
  onRemoveWhitelistEmail,
  onSaveWhitelist,
  whitelistSaving,
  onOpenActiveUsers,
  onUpdateNickname,
}) {
  const [ships, setShips] = useState([]);
  const [newShipName, setNewShipName] = useState("");
  const [addingShip, setAddingShip] = useState(false);
  const [addShipError, setAddShipError] = useState(null);
  // Track which row is currently being edited (keyed by email)
  const [editingId, setEditingId] = useState(null);

  const loadShips = useCallback(async () => {
    try {
      const { data } = await api.get("/ships");
      setShips(data);
    } catch {
      setShips([]);
    }
  }, []);

  useEffect(() => {
    loadShips();
  }, [loadShips]);

  const handleAddShip = async () => {
    const name = newShipName.trim();
    if (!name) return;
    setAddingShip(true);
    setAddShipError(null);
    try {
      await api.post("/ships", { name });
      setNewShipName("");
      loadShips();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setAddShipError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d?.msg ?? String(d)).join("; ")
          : "Failed to add ship"
      );
    } finally {
      setAddingShip(false);
    }
  };

  const handleDeleteShip = async (shipId) => {
    try {
      await api.delete(`/ships/${shipId}`);
      loadShips();
    } catch {
      // silently ignore
    }
  };

  // Sort entries by nickname (falling back to email), case-insensitive
  const sortedEntries = useMemo(() => {
    return [...whitelistEntries].sort((a, b) => {
      const nameA = (a.nickname && a.nickname !== a.email ? a.nickname : a.email).toLowerCase();
      const nameB = (b.nickname && b.nickname !== b.email ? b.nickname : b.email).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [whitelistEntries]);

  return (
    <div data-testid="admin-settings-panel">
      <div className="text-[11px] tracking-[0.3em] uppercase text-slate-500 mb-6 text-center">
        Admin Settings
      </div>

      {/* Active Sessions */}
      <div className="border border-slate-800 bg-[#121822]/60 p-5 max-w-md mx-auto mb-6">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-[11px] tracking-[0.3em] uppercase text-slate-400">
                Active Sessions
              </span>
            </div>
            <p className="text-slate-500 text-xs">
              View and manage all active user sessions. Kick individual users or clear all sessions at once.
            </p>
          </div>
          <button
            onClick={onOpenActiveUsers}
            data-testid="open-active-users"
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 border border-slate-600 bg-slate-800/60 text-slate-300 hover:border-orange-500 hover:text-orange-300 transition-colors text-[11px] tracking-widest uppercase"
          >
            <Users className="w-3.5 h-3.5" />
            Manage
          </button>
        </div>
      </div>

      {/* Email Whitelist */}
      <div className="border border-slate-800 bg-[#121822]/60 p-5 max-w-md mx-auto mb-6">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-3.5 h-3.5 text-orange-400" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-slate-400">
            Email Whitelist
          </span>
        </div>
        <p className="text-slate-500 text-xs mb-4">
          Only users whose email appears here can sign in. Add or remove emails
          below, then hit Save to persist changes. Click a nickname to edit it inline.
        </p>

        {/* Column headers */}
        {whitelistEntries.length > 0 && (
          <div className="flex items-center gap-2 px-3 mb-1">
            <span className="flex-1 text-[9px] tracking-[0.25em] uppercase text-slate-600">
              Nickname / Email
            </span>
          </div>
        )}

        {/* Current whitelist */}
        {whitelistEntries.length === 0 ? (
          <div
            className="border border-dashed border-slate-700 px-4 py-3 text-slate-600 text-xs mb-4"
            data-testid="whitelist-empty"
          >
            No emails whitelisted — all authenticated users can access the app.
          </div>
        ) : (
          <ul className="space-y-1.5 mb-4" data-testid="whitelist-list">
            {sortedEntries.map((entry) => (
              <WhitelistNicknameRow
                key={entry.email}
                entry={entry}
                onRemove={onRemoveWhitelistEmail}
                onUpdateNickname={onUpdateNickname}
                editingId={editingId}
                setEditingId={setEditingId}
              />
            ))}
          </ul>
        )}

        {/* Add email input */}
        <div className="flex items-center gap-2 mb-4">
          <input
            type="email"
            placeholder="new@example.com"
            value={whitelistInput}
            onChange={(e) => onWhitelistInputChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onAddWhitelistEmail()}
            data-testid="whitelist-input"
            disabled={!!editingId}
            className="flex-1 px-3 py-2 bg-[#0B0E14] border border-slate-700 text-white font-mono text-sm placeholder-slate-600 focus:outline-none focus:border-orange-500 transition-colors disabled:opacity-40"
          />
          <button
            onClick={onAddWhitelistEmail}
            data-testid="whitelist-add"
            disabled={!!editingId}
            className="px-3 py-2 border border-slate-600 bg-slate-800/60 text-slate-300 hover:border-orange-500 hover:text-orange-300 transition-colors text-[11px] tracking-widest uppercase disabled:opacity-40"
          >
            Add
          </button>
        </div>

        {/* Save button */}
        <button
          onClick={onSaveWhitelist}
          disabled={whitelistSaving || !!editingId}
          data-testid="whitelist-save"
          className="px-4 py-2 border border-orange-500 bg-orange-500/10 text-orange-300 hover:bg-orange-500 hover:text-black transition-colors text-[11px] tracking-widest uppercase disabled:opacity-40"
        >
          {whitelistSaving ? "Saving…" : "Save Whitelist"}
        </button>
      </div>

      {/* Manage Ships */}
      <div className="border border-slate-800 bg-[#121822]/60 p-5 max-w-md mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <Rocket className="w-3.5 h-3.5 text-orange-400" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-slate-400">
            Manage Ships
          </span>
        </div>
        <p className="text-slate-500 text-xs mb-4">
          Add or remove ships available in the Ship Claims dropdown. When the
          database is empty the claim form falls back to the default hardcoded list.
        </p>

        {/* Add ship input */}
        <div className="flex items-center gap-2 mb-4">
          <input
            type="text"
            placeholder="Ship name…"
            value={newShipName}
            onChange={(e) => setNewShipName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddShip()}
            data-testid="add-ship-input"
            className="flex-1 px-3 py-2 bg-[#0B0E14] border border-slate-700 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-orange-500 transition-colors"
          />
          <button
            onClick={handleAddShip}
            disabled={addingShip || !newShipName.trim()}
            data-testid="add-ship-btn"
            className="px-4 py-2 border border-slate-600 bg-slate-800/60 text-slate-300 hover:border-orange-500 hover:text-orange-300 transition-colors text-[11px] tracking-widest uppercase disabled:opacity-40"
          >
            {addingShip ? "Adding…" : "Add"}
          </button>
        </div>

        {addShipError && (
          <div className="px-3 py-2 border border-orange-500/30 bg-orange-500/10 text-orange-400 text-xs tracking-wide mb-3">
            {addShipError}
          </div>
        )}

        {/* Ship list */}
        {ships.length === 0 ? (
          <div
            className="border border-dashed border-slate-700 px-4 py-3 text-slate-600 text-xs"
            data-testid="ships-empty"
          >
            No ships in database — using default list for the claim form.
          </div>
        ) : (
          <ul className="space-y-1.5" data-testid="ships-list">
            {ships.map((ship) => (
              <li
                key={ship.id}
                className="flex items-center justify-between gap-3 px-3 py-2 border border-slate-700 bg-[#0B0E14]/60"
                data-testid="ship-entry"
              >
                <span className="text-slate-300 text-xs truncate">{ship.name}</span>
                <button
                  onClick={() => handleDeleteShip(ship.id)}
                  data-testid="ship-delete"
                  aria-label={`Remove ${ship.name}`}
                  className="flex-shrink-0 text-slate-600 hover:text-orange-400 transition-colors text-[10px] tracking-widest uppercase"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
